import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ImageUpload from "./ImageUpload";
import { Plus, Edit, Trash2, Star } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNotificationHeaders, NotificationHeader } from "@/hooks/useNotificationHeaders";
import { useNotificationFooters, NotificationFooter } from "@/hooks/useNotificationFooters";
import WysiwygEditor from "@/components/ui/wysiwyg-editor";
import { useToast } from "@/hooks/use-toast";

const HeaderFooterManager: React.FC = () => {
  const [isHeaderDialogOpen, setIsHeaderDialogOpen] = useState(false);
  const [isFooterDialogOpen, setIsFooterDialogOpen] = useState(false);
  const [editingHeader, setEditingHeader] = useState<NotificationHeader | null>(null);
  const [editingFooter, setEditingFooter] = useState<NotificationFooter | null>(null);
  const [headerForm, setHeaderForm] = useState({
    name: '',
    content: '',
    html_content: '',
    alignment: 'center',
    is_default: false,
    is_active: true
  });
  const [footerForm, setFooterForm] = useState({
    name: '',
    content: '',
    html_content: '',
    alignment: 'center',
    is_default: false,
    is_active: true
  });

  const { headers, isLoading: headersLoading, createHeader, updateHeader, deleteHeader } = useNotificationHeaders();
  const { footers, isLoading: footersLoading, createFooter, updateFooter, deleteFooter } = useNotificationFooters();
  const { toast } = useToast();

  const handleEditHeader = (header: NotificationHeader) => {
    setEditingHeader(header);
    setHeaderForm({
      name: header.name,
      content: header.content,
      html_content: header.html_content || '',
      alignment: (header as any).alignment || 'center',
      is_default: header.is_default,
      is_active: header.is_active
    });
    setIsHeaderDialogOpen(true);
  };

  const handleEditFooter = (footer: NotificationFooter) => {
    setEditingFooter(footer);
    setFooterForm({
      name: footer.name,
      content: footer.content,
      html_content: footer.html_content || '',
      alignment: (footer as any).alignment || 'center',
      is_default: footer.is_default,
      is_active: footer.is_active
    });
    setIsFooterDialogOpen(true);
  };

  const handleCreateHeader = () => {
    setEditingHeader(null);
    setHeaderForm({
      name: '',
      content: '',
      html_content: '',
      alignment: 'center',
      is_default: false,
      is_active: true
    });
    setIsHeaderDialogOpen(true);
  };

  const handleCreateFooter = () => {
    setEditingFooter(null);
    setFooterForm({
      name: '',
      content: '',
      html_content: '',
      alignment: 'center',
      is_default: false,
      is_active: true
    });
    setIsFooterDialogOpen(true);
  };

  const handleSaveHeader = async () => {
    try {
      if (editingHeader) {
        await updateHeader(editingHeader.id, headerForm);
        toast({ title: "Header updated successfully" });
      } else {
        await createHeader(headerForm);
        toast({ title: "Header created successfully" });
      }
      setIsHeaderDialogOpen(false);
    } catch (error) {
      toast({ title: "Error saving header", variant: "destructive" });
    }
  };

  const handleSaveFooter = async () => {
    try {
      if (editingFooter) {
        await updateFooter(editingFooter.id, footerForm);
        toast({ title: "Footer updated successfully" });
      } else {
        await createFooter(footerForm);
        toast({ title: "Footer created successfully" });
      }
      setIsFooterDialogOpen(false);
    } catch (error) {
      toast({ title: "Error saving footer", variant: "destructive" });
    }
  };

  const handleDeleteHeader = async (id: string) => {
    if (confirm('Are you sure you want to delete this header?')) {
      try {
        await deleteHeader(id);
        toast({ title: "Header deleted successfully" });
      } catch (error) {
        toast({ title: "Error deleting header", variant: "destructive" });
      }
    }
  };

  const handleDeleteFooter = async (id: string) => {
    if (confirm('Are you sure you want to delete this footer?')) {
      try {
        await deleteFooter(id);
        toast({ title: "Footer deleted successfully" });
      } catch (error) {
        toast({ title: "Error deleting footer", variant: "destructive" });
      }
    }
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="headers">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="headers">Headers ({headers.length})</TabsTrigger>
          <TabsTrigger value="footers">Footers ({footers.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="headers">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Notification Headers</CardTitle>
              <Button onClick={handleCreateHeader}>
                <Plus className="h-4 w-4 mr-2" />
                Create Header
              </Button>
            </CardHeader>
            <CardContent>
              {headersLoading ? (
                <div className="text-center py-8">Loading headers...</div>
              ) : (
                <div className="space-y-4">
                  {headers.map((header) => (
                    <div key={header.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{header.name}</h3>
                          {header.is_default && (
                            <Star className="h-4 w-4 text-yellow-500 fill-current" />
                          )}
                          <Badge variant={header.is_active ? "default" : "secondary"}>
                            {header.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditHeader(header)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteHeader(header.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">{header.content}</p>
                    </div>
                  ))}
                  {headers.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No headers created yet.
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="footers">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Notification Footers</CardTitle>
              <Button onClick={handleCreateFooter}>
                <Plus className="h-4 w-4 mr-2" />
                Create Footer
              </Button>
            </CardHeader>
            <CardContent>
              {footersLoading ? (
                <div className="text-center py-8">Loading footers...</div>
              ) : (
                <div className="space-y-4">
                  {footers.map((footer) => (
                    <div key={footer.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{footer.name}</h3>
                          {footer.is_default && (
                            <Star className="h-4 w-4 text-yellow-500 fill-current" />
                          )}
                          <Badge variant={footer.is_active ? "default" : "secondary"}>
                            {footer.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditFooter(footer)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteFooter(footer.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">{footer.content}</p>
                    </div>
                  ))}
                  {footers.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No footers created yet.
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Header Dialog */}
      <Dialog open={isHeaderDialogOpen} onOpenChange={setIsHeaderDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingHeader ? 'Edit Header' : 'Create Header'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="header-name">Name</Label>
              <Input
                id="header-name"
                value={headerForm.name}
                onChange={(e) => setHeaderForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter header name"
              />
            </div>
          <div className="space-y-4">
            <Tabs defaultValue="content">
              <TabsList>
                <TabsTrigger value="content">Content</TabsTrigger>
                <TabsTrigger value="images">Images</TabsTrigger>
              </TabsList>
              
              <TabsContent value="content" className="space-y-4">
                <div>
                  <Label htmlFor="header-content">Plain Text Content</Label>
                  <Textarea
                    id="header-content"
                    value={headerForm.content}
                    onChange={(e) => setHeaderForm(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Enter plain text header content"
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="header-alignment">Content Alignment</Label>
                  <Select value={headerForm.alignment} onValueChange={(value) => setHeaderForm(prev => ({ ...prev, alignment: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select alignment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="left">Left</SelectItem>
                      <SelectItem value="center">Center</SelectItem>
                      <SelectItem value="right">Right</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="header-html">HTML Content</Label>
                  <WysiwygEditor
                    value={headerForm.html_content}
                    onChange={(value) => setHeaderForm(prev => ({ ...prev, html_content: value }))}
                    placeholder="Enter HTML header content"
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="images" className="space-y-4">
                <ImageUpload
                  onImageUploaded={(url) => {
                    const imageTag = `<img src="${url}" alt="Header Image" style="max-width: 100%; height: auto;" />`;
                    setHeaderForm(prev => ({
                      ...prev,
                      html_content: prev.html_content + imageTag
                    }));
                  }}
                  bucket="notification-headers"
                  maxSize={3}
                />
              </TabsContent>
            </Tabs>
          </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={headerForm.is_default}
                  onChange={(e) => setHeaderForm(prev => ({ ...prev, is_default: e.target.checked }))}
                />
                Default Header
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={headerForm.is_active}
                  onChange={(e) => setHeaderForm(prev => ({ ...prev, is_active: e.target.checked }))}
                />
                Active
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsHeaderDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveHeader}>
                {editingHeader ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer Dialog */}
      <Dialog open={isFooterDialogOpen} onOpenChange={setIsFooterDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingFooter ? 'Edit Footer' : 'Create Footer'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="footer-name">Name</Label>
              <Input
                id="footer-name"
                value={footerForm.name}
                onChange={(e) => setFooterForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter footer name"
              />
            </div>
          <div className="space-y-4">
            <Tabs defaultValue="content">
              <TabsList>
                <TabsTrigger value="content">Content</TabsTrigger>
                <TabsTrigger value="images">Images</TabsTrigger>
              </TabsList>
              
              <TabsContent value="content" className="space-y-4">
                <div>
                  <Label htmlFor="footer-content">Plain Text Content</Label>
                  <Textarea
                    id="footer-content"
                    value={footerForm.content}
                    onChange={(e) => setFooterForm(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Enter plain text footer content"
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="footer-alignment">Content Alignment</Label>
                  <Select value={footerForm.alignment} onValueChange={(value) => setFooterForm(prev => ({ ...prev, alignment: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select alignment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="left">Left</SelectItem>
                      <SelectItem value="center">Center</SelectItem>
                      <SelectItem value="right">Right</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="footer-html">HTML Content</Label>
                  <WysiwygEditor
                    value={footerForm.html_content}
                    onChange={(value) => setFooterForm(prev => ({ ...prev, html_content: value }))}
                    placeholder="Enter HTML footer content"
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="images" className="space-y-4">
                <ImageUpload
                  onImageUploaded={(url) => {
                    const imageTag = `<img src="${url}" alt="Footer Image" style="max-width: 100%; height: auto;" />`;
                    setFooterForm(prev => ({
                      ...prev,
                      html_content: prev.html_content + imageTag
                    }));
                  }}
                  bucket="notification-footers"
                  maxSize={3}
                />
              </TabsContent>
            </Tabs>
          </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={footerForm.is_default}
                  onChange={(e) => setFooterForm(prev => ({ ...prev, is_default: e.target.checked }))}
                />
                Default Footer
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={footerForm.is_active}
                  onChange={(e) => setFooterForm(prev => ({ ...prev, is_active: e.target.checked }))}
                />
                Active
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsFooterDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveFooter}>
                {editingFooter ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HeaderFooterManager;